import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StarIcons } from '@/components/common';
import { SCORE_BANDS_DEFAULT } from '@/data/mockCatalog';
import type { NotifLang } from '@/types/admin.types';
import { LangBar, LANG_OPTIONS } from '../tabs/NotificationsTab/LangBar';

const LS_KEY = 'pmp_score_bands';
const LANG_KEYS = LANG_OPTIONS.map(l => l.id) as NotifLang[];
const emptyLabels = (): Record<NotifLang, string> =>
  Object.fromEntries(LANG_KEYS.map(k => [k, ''])) as Record<NotifLang, string>;

function withLabels<T extends { label: string; labels?: Record<string, string> }>(items: T[]) {
  return items.map(b => ({
    ...b,
    labels: b.labels ?? { ...emptyLabels(), es: b.label },
  }));
}

function loadBands() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return withLabels(JSON.parse(raw) as typeof SCORE_BANDS_DEFAULT);
  } catch { /* ignore */ }
  return withLabels(SCORE_BANDS_DEFAULT.map(b => ({ ...b })));
}

export function MadurezEditor() {
  const { t, i18n } = useTranslation();
  const [bands, setBands] = useState(loadBands);
  const [editing, setEditing] = useState(false);
  const [activeLang, setActiveLang] = useState<NotifLang>('es');

  function saveBands() {
    const toSave = bands.map(b => ({ ...b, label: b.labels?.es ?? b.label }));
    try { localStorage.setItem(LS_KEY, JSON.stringify(toSave)); } catch { /* ignore */ }
    setBands(toSave);
    setEditing(false);
  }

  const getRange = (i: number) => {
    const next = bands[i + 1];
    if (i === 0) return `< ${bands[1].minScore}`;
    if (!next) return `≥ ${bands[i].minScore}`;
    return `${bands[i].minScore} – ${next.minScore}`;
  };

  const uiLang = (i18n.language?.slice(0, 2) ?? 'es') as NotifLang;

  const filledCount = (labels: Record<string, string> | undefined) =>
    LANG_KEYS.filter(k => (labels?.[k] ?? '').trim() !== '').length;

  return (
    <div className="sc-editor-wrap">
      {editing ? (
        <>
          <div className="sc-editor-header">
            <span>
              <i className="fas fa-star" style={{ color: '#f59e0b' }} /> {t('admin.madurezEditTitle')}
            </span>
            <button
              className="sc-edit-btn sc-edit-btn--cancel"
              onClick={() => { setBands(withLabels(SCORE_BANDS_DEFAULT.map(b => ({ ...b })))); setEditing(false); }}
            >
              <i className="fas fa-times" />
            </button>
          </div>
          <LangBar active={activeLang} onSwitch={setActiveLang} group="madurez-label" />
          <div className="sc-edit-form">
            {bands.map((b, i) => (
              <div key={`band-${i}`} className="sc-edit-row">
                <div style={{ display: 'flex', gap: 3, alignItems: 'center', width: 52, flexShrink: 0 }}>
                  <StarIcons filled={b.stars} />
                </div>
                <input
                  className="sc-edit-label"
                  value={b.labels?.[activeLang] ?? ''}
                  placeholder={t('admin.madurezLabelPlaceholder')}
                  onChange={e => setBands(prev => prev.map((x, j) =>
                    j === i ? { ...x, labels: { ...emptyLabels(), ...x.labels, [activeLang]: e.target.value } } : x
                  ))}
                />
                {i > 0
                  ? <input
                      className="sc-edit-thresh"
                      type="number"
                      min={1}
                      max={3.9}
                      step={0.05}
                      value={b.minScore}
                      onChange={e => setBands(prev => prev.map((x, j) => j === i ? { ...x, minScore: parseFloat(e.target.value) || x.minScore } : x))}
                    />
                  : <span className="sc-edit-fixed">≥ 1.0</span>
                }
                <span className="sc-lang-count">{filledCount(b.labels)} / 7</span>
                <input
                  type="color"
                  className="sc-edit-color"
                  value={b.color}
                  onChange={e => setBands(prev => prev.map((x, j) => j === i ? { ...x, color: e.target.value } : x))}
                />
              </div>
            ))}
          </div>
          <div className="sc-edit-actions">
            <button className="sc-save-btn" onClick={saveBands}>
              <i className="fas fa-check" /> {t('common.save')}
            </button>
            <button
              className="sc-reset-btn"
              onClick={() => {
                setBands(withLabels(SCORE_BANDS_DEFAULT.map(b => ({ ...b }))));
                try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
                setEditing(false);
              }}
            >
              <i className="fas fa-undo" /> {t('admin.restoreDefaults')}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="sc-editor-header">
            <span>
              <i className="fas fa-star" style={{ color: '#f59e0b' }} /> {t('admin.madurezFrameworkTitle')}
            </span>
            <button className="sc-edit-btn" onClick={() => setEditing(true)}>
              <i className="fas fa-pen" /> {t('common.edit')}
            </button>
          </div>
          <div className="sc-readonly-list">
            {bands.map((b, i) => (
              <div key={`band-ro-${i}`} className="sc-readonly-item" style={{ borderLeft: `3px solid ${b.ring}` }}>
                <div style={{ display: 'flex', gap: 3, marginBottom: 2 }}>
                  <StarIcons filled={b.stars} />
                </div>
                <div className="sc-readonly-label" style={{ color: b.textColor, background: b.bg }}>
                  {b.labels?.[uiLang] || b.labels?.es || b.label}
                </div>
                <div className="sc-readonly-range">{getRange(i)}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
